// ============================================================================
// PlayerProfileSheet - tap a player at the table, CoinPoker-style
// ============================================================================
// Bottom-anchored sheet with:
//   - Header: avatar, name, LIVE stack + big blinds
//   - GAME STATS: PokerIntel aggregation (VPIP / PFR / WTSD / WSD, hands)
//   - CAREER: tournament career (winnings, biggest score, played, cashed,
//     final tables, wins) computed server-side from completed events
//   - Player NOTES + COLOR TAG (stored locally per viewer; the tag rings
//     that player's seat at every table you see them at)
// Data comes from POST /playerProfile - sanitized public stats only.
// ============================================================================
import React, { useEffect, useState } from 'react';
import gameApiService from '../services/gameApiService';
import gameItemsService from '../services/gameItemsService';
import { THROWABLE_ITEMS, TAUNT_ITEMS, ANNOUNCE_ITEMS, getItemPrice } from '../types/gameItemsTypes';

export const TAG_COLORS = ['#eab308', '#f97316', '#ef4444', '#d946ef', '#3b82f6', '#2dd4bf', '#22c55e'];

export function getSeatTag(uid: string): string | null {
  try { return localStorage.getItem(`pss:seatTag:${uid}`); } catch { return null; }
}

interface PlayerProfileSheetProps {
  playerId: string;
  playerName: string;
  photoURL?: string;
  chips: number;
  bigBlind: number;
  onClose: () => void;
  onTagChange?: () => void;   // parent re-reads tags to repaint seat rings
  // Throwables tray (CoinPoker-style): present when the viewer can throw
  tableId?: string;
  viewerId?: string;
  viewerName?: string;
}

const PlayerProfileSheet: React.FC<PlayerProfileSheetProps> = ({
  playerId, playerName, photoURL, chips, bigBlind, onClose, onTagChange,
  tableId, viewerId, viewerName,
}) => {
  const [throwBusy, setThrowBusy] = useState<string | null>(null);
  const [throwMsg, setThrowMsg] = useState<string | null>(null);
  const canThrow = !!tableId && !!viewerId && viewerId !== playerId;
  const throwItem = async (itemId: string) => {
    if (!canThrow || throwBusy) return;
    setThrowBusy(itemId);
    setThrowMsg(null);
    const r = await gameItemsService.useItem(
      tableId!, itemId, viewerId!, viewerName || 'Player', playerId, playerName, false,
    );
    setThrowBusy(null);
    if (r.success) {
      onClose(); // get out of the way - watch it fly
    } else {
      setThrowMsg(r.error || 'Could not throw that');
    }
  };
  const [tab, setTab] = useState<'game' | 'career'>('game');
  const [fmt, setFmt] = useState<'all' | 'tourney' | 'cash'>('all');
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [tag, setTag] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    gameApiService.playerProfile(playerId).then(r => {
      if (!cancelled) { setData(r); setLoading(false); }
    });
    try {
      setNote(localStorage.getItem(`pss:playerNote:${playerId}`) || '');
      setTag(localStorage.getItem(`pss:seatTag:${playerId}`));
    } catch { /* noop */ }
    return () => { cancelled = true; };
  }, [playerId]);

  const saveNote = (v: string) => {
    setNote(v);
    try { localStorage.setItem(`pss:playerNote:${playerId}`, v); } catch { /* noop */ }
  };
  const pickTag = (c: string | null) => {
    setTag(c);
    try {
      if (c) localStorage.setItem(`pss:seatTag:${playerId}`, c);
      else localStorage.removeItem(`pss:seatTag:${playerId}`);
    } catch { /* noop */ }
    if (onTagChange) onTagChange();
  };

  const bbCount = bigBlind > 0 ? (chips / bigBlind) : 0;
  // Game stats come in three flavours: ALL hands (the historical combined
  // counters), TOURNAMENT only, and CASH only. A tournament VPIP and a cash
  // VPIP describe very different players, so the card lets you switch.
  const gs = fmt === 'all' ? data?.gameStats
    : fmt === 'tourney' ? data?.tourneyStats
      : data?.cashStats;
  const career = data?.career;
  const money = (n: number) => career?.currency === 'playcoins'
    ? `${Math.round(n).toLocaleString()} PC`
    : `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);

  const statCell = (value: React.ReactNode, label: string) => (
    <div style={{ flex: 1, textAlign: 'center', padding: '10px 4px' }}>
      <div style={{ color: '#fff', fontSize: 21, fontWeight: 800 }}>{value}</div>
      <div style={{ color: '#8fa1b5', fontSize: 11.5, marginTop: 2, letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );

  const careerRow = (icon: string, value: React.ReactNode, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px' }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0,
      }}>{icon}</div>
      <div>
        <div style={{ color: '#fff', fontSize: 16.5, fontWeight: 800 }}>{value}</div>
        <div style={{ color: '#8fa1b5', fontSize: 11.5 }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9350,
        background: 'rgba(0,0,0,0.6)', display: 'flex',
        alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(100vw, 560px)', maxHeight: '82vh', overflowY: 'auto',
          background: 'linear-gradient(180deg, rgba(20,12,14,0.99), rgba(8,10,16,0.99))',
          borderRadius: '18px 18px 0 0', border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none', boxShadow: '0 -18px 60px rgba(0,0,0,0.7)',
          color: '#e2e8f0',
          padding: '14px 20px calc(24px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Grab bar + close */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ flex: 1 }} />
          <div style={{ width: 44, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ flex: 1, textAlign: 'right' }}>
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', color: '#94a3b8',
              fontSize: 22, cursor: 'pointer', lineHeight: 1,
            }}>×</button>
          </div>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 62, height: 62, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            border: `2.5px solid ${tag || 'rgba(212,175,55,0.55)'}`,
            background: '#1a2332', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 800, color: '#7dd3fc',
          }}>
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (playerName || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 19, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {playerName}
            </div>
            <div style={{ marginTop: 3, fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: '#fbbf24' }}>{Math.round(chips).toLocaleString()}</span>
              {bigBlind > 0 && (
                <span style={{ color: '#60a5fa', marginLeft: 6 }}>({bbCount >= 100 ? Math.round(bbCount) : bbCount.toFixed(1)}BB)</span>
              )}
            </div>
            {gs && gs.handsPlayed > 0 && (
              <div style={{
                display: 'inline-block', marginTop: 5, fontSize: 11, fontWeight: 700,
                background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: '3px 10px', color: '#cbd5e1',
              }}>
                {gs.handsPlayed.toLocaleString()} hands tracked
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', marginTop: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {([['game', 'Game Stats'], ['career', 'Career Stats']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                flex: 1, background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '10px 0', fontSize: 14.5, fontWeight: 700,
                color: tab === id ? '#fff' : '#7c8aa0',
                borderBottom: tab === id ? '3px solid #ef4444' : '3px solid transparent',
              }}
            >{label}</button>
          ))}
        </div>

        {/* Body */}
        {loading ? (
          <div style={{ padding: '28px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
        ) : data?.isBot ? (
          <div style={{ padding: '28px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13.5 }}>
            House player - stats aren't tracked for the house.
          </div>
        ) : tab === 'game' ? (
          <div style={{ paddingTop: 10 }}>
            <div style={{ textAlign: 'right', color: '#8fa1b5', fontSize: 11, letterSpacing: '0.06em', marginBottom: 8 }}>
              Powered by <b style={{ color: '#e2e8f0' }}>PokerIntel</b> · Lifetime
            </div>
            {/* FORMAT SWITCH - tournament and cash play are read separately */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {([['all', 'All'], ['tourney', '🏆 Tournament'], ['cash', '💵 Cash']] as const).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFmt(id)}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 12, fontWeight: 800,
                    letterSpacing: '0.02em', cursor: 'pointer',
                    background: fmt === id ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${fmt === id ? 'rgba(239,68,68,0.7)' : 'rgba(255,255,255,0.12)'}`,
                    color: fmt === id ? '#fff' : '#8fa1b5',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              {statCell(pct(gs?.vpipPct), 'VPIP')}
              {statCell(pct(gs?.pfrPct), 'PFR')}
              {statCell(pct(gs?.wtsdPct), 'WTSD')}
              {statCell(pct(gs?.wsdPct), 'WSD')}
            </div>
            {/* WTSD/WSD bar, CoinPoker style */}
            {gs?.wtsdPct != null && gs?.wsdPct != null && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 800 }}>
                  <span style={{ color: '#fb923c' }}>{gs.wtsdPct}% <span style={{ color: '#8fa1b5', fontWeight: 600 }}>WTSD</span></span>
                  <span style={{ color: '#34d399' }}><span style={{ color: '#8fa1b5', fontWeight: 600 }}>WSD</span> {gs.wsdPct}%</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.1)', marginTop: 6, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${Math.min(100, gs.wtsdPct)}%`, background: 'linear-gradient(90deg, #059669, #fb923c)' }} />
                </div>
              </div>
            )}
            {(!gs || gs.handsPlayed === 0) && (
              <div style={{ padding: '18px 0 6px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                {fmt === 'all'
                  ? 'No tracked hands yet.'
                  : `No ${fmt === 'tourney' ? 'tournament' : 'cash game'} hands tracked yet.`}
                {fmt !== 'all' && (data?.gameStats?.handsPlayed || 0) > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11.5, color: '#64748b' }}>
                    Hands played before the split was added are counted under “All”.
                  </div>
                )}
              </div>
            )}
            {gs && gs.handsPlayed > 0 && (
              <div style={{ marginTop: 10, textAlign: 'center', color: '#64748b', fontSize: 11.5 }}>
                {gs.handsPlayed.toLocaleString()}{' '}
                {fmt === 'tourney' ? 'tournament' : fmt === 'cash' ? 'cash game' : ''} hands
              </div>
            )}
            {gs?.netBB != null && gs.handsPlayed > 0 && (
              <div style={{ marginTop: 12, textAlign: 'center', color: '#8fa1b5', fontSize: 12 }}>
                Lifetime net: <b style={{ color: gs.netBB >= 0 ? '#34d399' : '#f87171' }}>
                  {gs.netBB >= 0 ? '+' : ''}{gs.netBB.toLocaleString()} BB
                </b>
              </div>
            )}
          </div>
        ) : (
          <div style={{ paddingTop: 8 }}>
            <div style={{ color: '#8fa1b5', fontSize: 11.5, letterSpacing: '0.08em', margin: '6px 0 2px', fontWeight: 700 }}>
              TOURNAMENT CAREER
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
              {careerRow('💰', money(career?.totalWinnings || 0), 'Total Won')}
              {careerRow('🚀', money(career?.biggestScore || 0), 'Biggest Score')}
              {careerRow('🏆', (career?.played || 0).toLocaleString(), 'Tournaments Played')}
              {careerRow('💵', <>{career?.cashed || 0} <span style={{ color: '#8fa1b5', fontWeight: 600 }}>({career?.cashRatePct || 0}%)</span></>, 'Tournaments Cashed')}
              {careerRow('🎯', career?.finalTables || 0, 'Final Tables')}
              {careerRow('🥇', career?.won || 0, 'Tournaments Won')}
            </div>
          </div>
        )}

        {/* Notes + color tag */}
        <textarea
          value={note}
          onChange={e => saveNote(e.target.value)}
          placeholder="Type here for player notes"
          style={{
            width: '100%', minHeight: 74, marginTop: 16, resize: 'vertical',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            // 16px minimum: iOS Safari zooms the whole page into any input
            // with a smaller font - the classic mobile jump.
            borderRadius: 12, color: '#e2e8f0', fontSize: 16, padding: '12px 14px',
            outline: 'none', fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => pickTag(null)}
            title="Remove color tag"
            style={{
              width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
              background: 'transparent', border: `2px solid ${tag == null ? '#fff' : 'rgba(255,255,255,0.5)'}`,
              color: '#fff', fontSize: 16, lineHeight: 1,
            }}
          >⃠</button>
          {TAG_COLORS.map(c => (
            <button
              key={c}
              onClick={() => pickTag(c)}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                background: c, border: tag === c ? '3px solid #fff' : '2px solid transparent',
                boxShadow: tag === c ? `0 0 12px ${c}` : 'none',
              }}
            />
          ))}
        </div>
        <div style={{ color: '#8fa1b5', fontSize: 11.5, marginTop: 8 }}>
          You can color tag this player - the tag rings their seat wherever you play them.
        </div>

        {/* 🎯 THROWABLES - tap to throw it at this player (CoinPoker-style) */}
        {canThrow && (
          <>
            <div style={{
              display: 'flex', gap: 10, marginTop: 16, overflowX: 'auto',
              padding: '10px 2px', borderTop: '1px solid rgba(255,255,255,0.08)',
              WebkitOverflowScrolling: 'touch',
            }}>
              {THROWABLE_ITEMS.map(item => {
                const price = getItemPrice(item.id, false);
                return (
                  <button
                    key={item.id}
                    onClick={() => throwItem(item.id)}
                    disabled={!!throwBusy}
                    title={`${item.name}${price > 0 ? ` - $${price.toFixed(2)}` : ''}`}
                    style={{
                      flexShrink: 0, width: 60, borderRadius: 12, cursor: 'pointer',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      padding: '8px 0 6px', textAlign: 'center',
                      opacity: throwBusy && throwBusy !== item.id ? 0.5 : 1,
                      transform: throwBusy === item.id ? 'scale(0.9)' : 'none',
                      transition: 'transform 120ms ease',
                    }}
                  >
                    <div style={{ fontSize: 26, lineHeight: 1.1 }}>{item.emoji}</div>
                    <div style={{ color: '#8fa1b5', fontSize: 10, fontWeight: 700, marginTop: 3 }}>
                      {price > 0 ? `$${price.toFixed(2)}` : 'FREE'}
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ color: '#8fa1b5', fontSize: 11.5, marginTop: 2 }}>
              Tap to throw it at {playerName} - everyone at the table sees it land.
            </div>

            {/* 🆓 FREE TAUNTS - no charge, pure disrespect. They FLY. */}
            <div style={{
              color: '#67e8f9', fontSize: 11, fontWeight: 800, letterSpacing: '0.16em',
              marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)',
            }}>
              FREE TAUNTS · ALWAYS ON THE HOUSE
            </div>
            <div style={{
              display: 'flex', gap: 8, marginTop: 8, overflowX: 'auto',
              padding: '2px 2px 8px', WebkitOverflowScrolling: 'touch',
            }}>
              {TAUNT_ITEMS.map(item => (
                <button
                  key={item.id}
                  onClick={() => throwItem(item.id)}
                  disabled={!!throwBusy}
                  title={item.description}
                  style={{
                    flexShrink: 0, borderRadius: 12, cursor: 'pointer',
                    background: 'rgba(103,232,249,0.06)', border: '1px solid rgba(103,232,249,0.3)',
                    padding: '7px 11px', textAlign: 'center', whiteSpace: 'nowrap',
                    opacity: throwBusy && throwBusy !== item.id ? 0.5 : 1,
                    transform: throwBusy === item.id ? 'scale(0.94)' : 'none',
                    transition: 'transform 120ms ease',
                  }}
                >
                  <span style={{ fontSize: 18, marginRight: 6, verticalAlign: 'middle' }}>{item.emoji}</span>
                  <span style={{ color: '#cffafe', fontSize: 12, fontWeight: 800, verticalAlign: 'middle' }}>
                    {item.name}
                  </span>
                </button>
              ))}
            </div>

            {/* 📢 PAID CALL-OUTS - cinematic table-wide announcements */}
            <div style={{
              color: '#d4af37', fontSize: 11, fontWeight: 800, letterSpacing: '0.16em',
              marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)',
            }}>
              📢 CALL-OUTS · ANNOUNCED TO THE WHOLE TABLE
            </div>
            <div style={{
              display: 'flex', gap: 8, marginTop: 8, overflowX: 'auto',
              padding: '2px 2px 8px', WebkitOverflowScrolling: 'touch',
            }}>
              {ANNOUNCE_ITEMS.map(item => {
                const price = getItemPrice(item.id, false);
                const preview = (item.message || '')
                  .replace(/\{target\}/g, playerName)
                  .replace(/\{sender\}/g, viewerName || 'You');
                return (
                  <button
                    key={item.id}
                    onClick={() => throwItem(item.id)}
                    disabled={!!throwBusy}
                    title={preview}
                    style={{
                      flexShrink: 0, borderRadius: 12, cursor: 'pointer',
                      background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.4)',
                      padding: '8px 12px', textAlign: 'left', maxWidth: 190,
                      opacity: throwBusy && throwBusy !== item.id ? 0.5 : 1,
                      transform: throwBusy === item.id ? 'scale(0.94)' : 'none',
                      transition: 'transform 120ms ease',
                    }}
                  >
                    <div style={{ color: '#fde68a', fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {item.emoji} {item.name} · ${price.toFixed(2)}
                    </div>
                    <div style={{
                      color: '#8fa1b5', fontSize: 10.5, marginTop: 3, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170,
                    }}>
                      "{preview}"
                    </div>
                  </button>
                );
              })}
            </div>
            {throwMsg && (
              <div style={{ color: '#f87171', fontSize: 12, marginTop: 6, fontWeight: 700 }}>{throwMsg}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerProfileSheet;
